function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function daysBetween(now, target) {
  return Math.round((startOfDay(now) - startOfDay(target)) / 86400000);
}

export function formatRelativeTime(timestamp) {
  if (!timestamp) return "";
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMinutes = Math.max(0, Math.round((now - then) / 60000));
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  const diffWeeks = Math.round(diffDays / 7);
  return `${diffWeeks}w ago`;
}

export function isToday(timestamp) {
  if (!timestamp) return false;
  return daysBetween(new Date(), new Date(timestamp)) === 0;
}

function activityRecencyLabel(connection) {
  if (connection?.workspace_summary?.shared_updated_today) {
    return "Both updated today";
  }
  if (connection?.workspace_summary?.partner_updated_today) {
    return "Partner updated today";
  }
  if (connection?.last_activity_at && isToday(connection.last_activity_at)) {
    return "Showed up today";
  }
  if (connection?.last_activity_at) {
    return formatRelativeTime(connection.last_activity_at);
  }
  if (connection?.activity_status === "slipping") {
    return "Quiet now";
  }
  return "Quiet lately";
}

function activityHighlight(connection) {
  if (connection?.workspace_summary?.shared_updated_today) return "active";
  if (connection?.active_sprint?.id && isToday(connection?.last_activity_at)) return "active";
  if (connection?.workspace_summary?.partner_updated_today) return "recent";
  if (connection?.activity_status === "active" || isToday(connection?.last_activity_at)) return "recent";
  return "quiet";
}

function statusTone(connection, index = 0) {
  const palette = ["#29c284", "#ff8a17", "#17345d", "#31c28a", "#ff8a17", "#254b79"];
  if (connection?.workspace_summary?.shared_updated_today) return "#29c284";
  if (connection?.active_sprint?.id) return "#17345d";
  if (connection?.workspace_summary?.partner_updated_today || isToday(connection?.last_activity_at)) {
    return "#29c284";
  }
  return palette[index % palette.length];
}

export function sortConnectionsForNetwork(connections = []) {
  return [...connections].sort((left, right) => {
    const rank = (item) => {
      if (item?.active_sprint?.id) return 0;
      if (item?.workspace_summary?.partner_updated_today) return 1;
      if (item?.activity_status === "active") return 1;
      return 2;
    };
    const diff = rank(left) - rank(right);
    if (diff !== 0) return diff;
    const leftTime = left?.last_activity_at ? new Date(left.last_activity_at).getTime() : 0;
    const rightTime = right?.last_activity_at ? new Date(right.last_activity_at).getTime() : 0;
    return rightTime - leftTime;
  });
}

export function mapConnectionsToPeople(connections = []) {
  return sortConnectionsForNetwork(connections).slice(0, 8).map((connection, index) => ({
    id: String(connection?.id || connection?.other_user?.id || ""),
    connectionId: connection?.id || null,
    sprintId: connection?.active_sprint?.id || null,
    userId: connection?.other_user?.id || null,
    name: connection?.other_user?.username || "TrustBud",
    image: connection?.other_user?.profile_image || "",
    statusLabel: activityRecencyLabel(connection),
    ringColor: statusTone(connection, index),
    textColor: activityHighlight(connection) === "active" ? "#22b573" : "#7b8794",
  }));
}

function getSectionTitle(timestamp) {
  if (!timestamp) return "Earlier this week";
  const diff = daysBetween(new Date(), new Date(timestamp));
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  return "Earlier this week";
}

function normalizeActivityType(type) {
  if (type === "message_received") return "update";
  if (type === "connection_request") return "relationship_activity";
  if (type === "habit_completed" || type === "action_completed" || type === "small_version_counted") return "action_completed";
  if (type === "checkin_completed") return "checkin";
  if (type === "encouragement_sent") return "acknowledgment";
  if (type === "weekly_summary_ready") return "goal";
  return type || "";
}

function getTypeMeta(type) {
  type = normalizeActivityType(type);
  if (type === "action_completed") {
    return { tagLabel: "Habit", tagColor: "#17345d", tagBg: "rgba(23, 52, 93, 0.08)" };
  }
  if (type === "update" || type === "repair" || type === "acknowledgment") {
    return { tagLabel: "Update", tagColor: "#1fbf8f", tagBg: "rgba(31, 191, 143, 0.09)" };
  }
  if (type === "checkin") {
    return { tagLabel: "Check-in", tagColor: "#1fbf8f", tagBg: "rgba(31, 191, 143, 0.09)" };
  }
  if (type === "reentry") {
    return { tagLabel: "Restart", tagColor: "#ff8a17", tagBg: "rgba(255, 138, 23, 0.1)" };
  }
  if (type === "action_created") {
    return { tagLabel: "New habit", tagColor: "#ff8a17", tagBg: "rgba(255, 138, 23, 0.1)" };
  }
  if (type === "sprint_start") {
    return { tagLabel: "Accountability", tagColor: "#17345d", tagBg: "rgba(23, 52, 93, 0.08)" };
  }
  if (type === "sprint_complete") {
    return { tagLabel: "Finished", tagColor: "#7b8794", tagBg: "rgba(123, 135, 148, 0.1)" };
  }
  return { tagLabel: "Activity", tagColor: "#7b8794", tagBg: "rgba(123, 135, 148, 0.08)" };
}

export function mapActivityItems(items = []) {
  return [...items]
    .sort(
      (left, right) =>
        new Date(right?.created_at || 0).getTime() - new Date(left?.created_at || 0).getTime()
    )
    .slice(0, 8)
    .map((item, index) => {
      const type = normalizeActivityType(item?.display_type || item?.type);
      const meta = getTypeMeta(type);
      const ringColor = statusTone(
        {
          active_sprint: { id: item?.sprint_id || null },
          workspace_summary: null,
          last_activity_at: item?.created_at || null,
        },
        index
      );
      const safeDetail = item?.display_detail ?? item?.detail ?? "";
      const detail = type === "update" || type === "repair" ? (safeDetail ? `"${safeDetail}"` : "") : safeDetail;
      const opensChallengeFromRow =
        type === "sprint_start" && item?.action_kind === "open_sprint" && item?.sprint_id;

      return {
        id: String(item?.id || ""),
        actorName: item?.actor?.username || "Someone",
        actorImage: item?.actor?.profile_image || "",
        headline: item?.display_summary || item?.summary || "Shared activity",
        detail,
        timeLabel: formatRelativeTime(item?.created_at),
        sectionTitle: getSectionTitle(item?.created_at),
        tagLabel: meta.tagLabel,
        tagColor: meta.tagColor,
        tagBg: meta.tagBg,
        ctaLabel: opensChallengeFromRow
          ? null
          : item?.action_kind === "open_sprint"
            ? "Open Accountability"
            : item?.action_kind === "encourage"
              ? "Encourage"
              : item?.cta_label || "Reply",
        actionKind: item?.action_kind || "",
        prefillMessage: item?.prefill_message || "",
        sprintId: item?.sprint_id || null,
        connectionId: item?.connection_id || null,
        userId: item?.partner_user_id || null,
        createdAt: item?.created_at || null,
        ringColor,
      };
    });
}

export function groupActivityItems(items = []) {
  const grouped = items.reduce((acc, item) => {
    const key = item.sectionTitle || "Earlier this week";
    acc[key] = [...(acc[key] || []), item];
    return acc;
  }, {});

  return ["Today", "Yesterday", "Earlier this week"]
    .filter((label) => Array.isArray(grouped[label]) && grouped[label].length)
    .map((label) => ({ label, items: grouped[label] }));
}

function percentageChange(today, yesterday) {
  if (!yesterday) {
    return today > 0 ? "+100%" : "0%";
  }
  const change = Math.round(((today - yesterday) / yesterday) * 100);
  return `${change >= 0 ? "+" : ""}${change}%`;
}

export function buildTodayAtAGlance(activityItems = []) {
  const now = new Date();
  const todayItems = activityItems.filter(
    (item) => item?.createdAt && daysBetween(now, new Date(item.createdAt)) === 0
  );
  const yesterdayItems = activityItems.filter(
    (item) => item?.createdAt && daysBetween(now, new Date(item.createdAt)) === 1
  );

  const todayMoved = new Set(todayItems.map((item) => item.userId || item.actorName).filter(Boolean)).size;
  const yesterdayMoved = new Set(
    yesterdayItems.map((item) => item.userId || item.actorName).filter(Boolean)
  ).size;
  const todayActions = todayItems.filter((item) => item.tagLabel === "Habit").length;
  const yesterdayActions = yesterdayItems.filter((item) => item.tagLabel === "Habit").length;
  const todayUpdates = todayItems.filter(
    (item) => item.tagLabel === "Update" || item.tagLabel === "Check-in"
  ).length;
  const yesterdayUpdates = yesterdayItems.filter(
    (item) => item.tagLabel === "Update" || item.tagLabel === "Check-in"
  ).length;

  return [
    {
      id: "moved",
      label: "Active today",
      value: String(todayMoved),
      change: `${percentageChange(todayMoved, yesterdayMoved)} vs yesterday`,
      accent: "#1fbf8f",
      soft: "rgba(31, 191, 143, 0.12)",
    },
    {
      id: "actions",
      label: "Habits completed",
      value: String(todayActions),
      change: `${percentageChange(todayActions, yesterdayActions)} vs yesterday`,
      accent: "#17345d",
      soft: "rgba(23, 52, 93, 0.12)",
    },
    {
      id: "updates",
      label: "Notes shared",
      value: String(todayUpdates),
      change: `${percentageChange(todayUpdates, yesterdayUpdates)} vs yesterday`,
      accent: "#ff9a2f",
      soft: "rgba(255, 154, 47, 0.14)",
    },
  ];
}
